/**
 * A LIST THAT DID NOT LOAD (P38, task 2 — audit S2-18, reference §11/§12)
 *
 * =========================================================================
 * WHAT WAS THERE
 * =========================================================================
 * Four sites, one shape: `<div className="… bg-red-50 text-red-700">Failed to
 * load drafts.</div>` — `ReviewBoard.tsx:112`, `Authorizations.tsx:76`,
 * `AuditTrail.tsx:136`, `ApprovalQueue.tsx:509`, plus `TicketBoard.tsx`, which
 * the audit did not list and which had it too. **No retry, no code, no time.**
 *
 * The reader's only move was to reload the whole page, which throws away their
 * filters, their sort and their column layout — so the cheapest recovery from a
 * transient 502 cost them the state they had built up, and the report they could
 * give IT was "it said failed to load".
 *
 * =========================================================================
 * WHY THIS IS A COMPONENT AND NOT FIVE CALLS TO `ErrorBanner`
 * =========================================================================
 * `ErrorBanner` (P36) already draws the icon, the *Try again* and the code line.
 * What the five sites would each have had to get right is the part that is easy
 * to get subtly wrong and impossible to see: **which code, and which time.**
 *
 * - The CODE that helps is not only the HTTP status. `ApiRequestError` carries
 *   the envelope's `error.code` and the server's `meta.requestId`, and the
 *   request id is the one string that finds this exact failure in the log. A
 *   banner saying `500` names a class of problem; one saying
 *   `500 · DB_ERROR · req 7f3a…` names an incident.
 * - The TIME must be when it FAILED, not when the banner rendered. `ErrorBanner`
 *   defaults `at` to `new Date()`, which is right for a one-shot and wrong here:
 *   a banner sits on screen until somebody retries, re-rendering as the reader
 *   filters around it, and a clock that ticks forward tells them the failure
 *   just happened when it happened four minutes ago. react-query knows —
 *   `errorUpdatedAt` — so it is passed in and never derived.
 *
 * One component, so those two cannot be got right in four places and wrong in
 * the fifth.
 *
 * =========================================================================
 * THE RETRY IS `refetch`, AND IT IS THE QUERY'S OWN
 * =========================================================================
 * Not `window.location.reload()` (which loses the reader's URL state for the
 * same reason the old banner's absence did) and not `invalidateQueries` (which
 * is a cache instruction and does nothing visible when the query has no
 * observer). `refetch` from the very hook that failed re-runs THAT request, so
 * *Try again* is proven by the network tab showing a second call — which is how
 * this phase proved it, against a blocked request.
 */
import { ApiRequestError } from '../../services/api';
import { ErrorBanner } from './Banner';
import { isoSeconds } from '../../lib/dates';

/**
 * `500 · DB_ERROR · req 7f3a1b…`, or as much of it as the failure carried.
 *
 * A non-`ApiRequestError` (a network failure, an aborted fetch) has no status at
 * all, and saying `undefined` would be worse than saying nothing: the banner then
 * carries the time alone, which is still enough to correlate with a log.
 */
export function failureCode(error: unknown): string | null {
    if (error instanceof ApiRequestError) {
        const parts = [String(error.status)];
        if (error.error?.code) parts.push(error.error.code);
        if (error.requestId) parts.push(`req ${error.requestId}`);
        return parts.join(' · ');
    }
    if (error instanceof Error && error.message) return error.message.slice(0, 80);
    return null;
}

export interface LoadErrorProps {
    /**
     * What did not load, in the reader's words — "the review queue", "drafts".
     * Plain words: what is missing, never which layer threw (§12).
     */
    what: string;
    error: unknown;
    /** react-query's `errorUpdatedAt`. Milliseconds; 0 means it never failed. */
    at?: number;
    /** The failing query's own `refetch`. */
    onRetry?: () => void;
    className?: string;
}

export function LoadError({ what, error, at, onRetry, className = 'mt-4' }: LoadErrorProps) {
    return (
        <ErrorBanner
            title={`${what} could not be loaded.`}
            code={failureCode(error)}
            at={at ? isoSeconds(at) : undefined}
            onRetry={onRetry}
            className={className}
        >
            <p>
                Nothing was changed and nothing was sent — this is a read that did not come back.
                Try again; if it keeps failing, send the code below to IT.
            </p>
        </ErrorBanner>
    );
}
